import { useEffect, useState, type ReactNode } from 'react';
import {
  Sun, Moon, KeyRound, LifeBuoy, LogOut, Download, ShieldCheck, Info,
  Monitor, Smartphone, X, Trash2, Siren, Copy, AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import {
  Avatar, Badge, Button, Card, CardBody, CardHeader, Field, Input, KV, Modal,
} from '../components/ui';
import { PasswordInput, PasswordStrength, scorePassword } from '../components/PasswordInput';
import { fmtDate, fmtDateTime, initials, ROLE_LABEL } from '../lib/format';
import { useTheme } from '../lib/theme';
import { useToast } from '../lib/toast';
import { api, ApiError, downloadFile, setToken } from '../api/client';
import type { CurrentUser } from '../api/types';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, useUpdateMe, useSessions, useRevokeSession, useAppConfig, useUpdateAppConfig } from '../api/queries';
import { DatenschutzModal, ImpressumModal } from '../components/LegalModals';

export function SettingsScreen({ user, onLogout }: { user: CurrentUser; onLogout: () => void }) {
  const { theme, toggle } = useTheme();
  const [pwOpen, setPwOpen] = useState(false);
  const [dsOpen, setDsOpen] = useState(false);
  const [impOpen, setImpOpen] = useState(false);
  const toast = useToast();

  const logoutAll = async () => {
    try {
      await api.post('/auth/logout-all');
      setToken(null);
      toast.push('Auf allen Geräten abgemeldet');
      onLogout();
    } catch {
      toast.push('Konnte nicht abmelden', 'warn');
    }
  };

  return (
    <div className="page-fade">
      <PageHeader title="Einstellungen" subtitle="Dein Profil und die App" />
      <div className="settings-cols" style={{ alignItems: 'start' }}>
        <ProfileCard user={user} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card>
            <CardHeader>Darstellung</CardHeader>
            <CardBody>
              <Row label="Design" sub="Hell oder Dunkel">
                <div className="segmented">
                  {([['light', 'Hell', <Sun size={14} />], ['dark', 'Dunkel', <Moon size={14} />]] as const).map(([v, l, ic]) => (
                    <button
                      key={v}
                      className={theme === v ? 'active' : ''}
                      onClick={() => { if (theme !== v) toggle(); }}
                      type="button"
                    >
                      {ic}{l}
                    </button>
                  ))}
                </div>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>Sicherheit</CardHeader>
            <CardBody>
              <Row label="Passwort" sub={`Zuletzt geändert am ${fmtDate(user.passwortGesetzt)}`}>
                <Button variant="secondary" size="sm" icon={<KeyRound size={14} />} onClick={() => setPwOpen(true)}>
                  Passwort ändern
                </Button>
              </Row>
              {user.rolle === 'admin' && (
                <Row label="Recovery-Codes" sub="Neue Codes generieren — alte werden ungültig">
                  <RegenerateCodesButton />
                </Row>
              )}
              {user.rolle === 'admin' && (
                <Row label="Notfall-Schlüssel" sub="Master-Token für die Ersteinrichtung rotieren">
                  <RotateMasterTokenButton />
                </Row>
              )}
              <SessionsList />
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Button variant="ghost" icon={<LogOut size={14} />} onClick={logoutAll}>Auf allen Geräten abmelden</Button>
                <Button variant="ghost" icon={<LogOut size={14} />} onClick={onLogout} className="text-danger">Abmelden</Button>
              </div>
            </CardBody>
          </Card>

          {user.rolle === 'admin' && (
            <UnionBeitragCard />
          )}

          {user.rolle === 'admin' && (
            <Card>
              <CardHeader>Daten & Sicherung</CardHeader>
              <CardBody>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 14px' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--success-soft)', color: 'var(--success)', display: 'grid', placeItems: 'center' }}>
                    <ShieldCheck size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Automatische Sicherung</div>
                    <div className="text-tertiary" style={{ fontSize: 12.5 }}>Cloudflare D1 Snapshots, täglich</div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download size={14} />}
                    onClick={async () => {
                      try {
                        await downloadFile('/backup.csv', 'backup.csv');
                        toast.push('Backup heruntergeladen');
                      } catch {
                        toast.push('Backup fehlgeschlagen', 'warn');
                      }
                    }}
                  >
                    Manuelles Backup
                  </Button>
                </div>
                <div className="muted-card" style={{ fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Info size={14} className="text-tertiary" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Zusätzlich werden täglich automatische Snapshots in der EU erstellt. Ein manuelles
                    Backup lädt alle Daten als CSV herunter.
                  </span>
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>System</CardHeader>
            <CardBody>
              <div className="kv-list">
                <KV k="Version">1.2.0 · Stand Juli 2026</KV>
                <KV k="Speicherort">EU-Rechenzentren (DSGVO-konform)</KV>
                <KV k="Datenschutz">
                  <button onClick={() => setDsOpen(true)} className="text-accent" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>Datenschutzerklärung</button>
                  {' · '}
                  <button onClick={() => setImpOpen(true)} className="text-accent" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>Impressum</button>
                </KV>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <DatenschutzModal open={dsOpen} onClose={() => setDsOpen(false)} />
      <ImpressumModal open={impOpen} onClose={() => setImpOpen(false)} />
    </div>
  );
}

function UnionBeitragCard() {
  const cfg = useAppConfig();
  const update = useUpdateAppConfig();
  const toast = useToast();
  const [val, setVal] = useState<string>('');

  useEffect(() => {
    if (cfg.data?.union_beitrag_jahr !== undefined) {
      setVal(cfg.data.union_beitrag_jahr);
    }
  }, [cfg.data?.union_beitrag_jahr]);

  const initial = cfg.data?.union_beitrag_jahr ?? '';
  const parsed = Number(val.replace(',', '.'));
  const valid = val.trim() !== '' && !Number.isNaN(parsed) && parsed >= 0;
  const dirty = val.trim() !== initial;

  const save = () => {
    if (!valid || !dirty) return;
    update.mutate(
      { union_beitrag_jahr: parsed.toFixed(2) },
      {
        onSuccess: () => toast.push('Union-Beitrag gespeichert'),
        onError: () => toast.push('Konnte nicht speichern', 'warn'),
      },
    );
  };

  return (
    <Card>
      <CardHeader>Union-Beitrag</CardHeader>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <Field label="Beitrag pro Mitglied / Jahr (€)" hint="Wird für Abrechnungsformulare pro Sektion verwendet.">
            <Input
              value={val}
              onChange={(e) => setVal(e.target.value)}
              inputMode="decimal"
              className="t-mono"
              placeholder="2.50"
              style={{ maxWidth: 140 }}
            />
          </Field>
          <Button
            variant="primary"
            size="sm"
            onClick={save}
            disabled={!valid || !dirty || update.isPending}
          >
            Speichern
          </Button>
        </div>
        <div className="muted-card" style={{ fontSize: 12.5, marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Info size={14} className="text-tertiary" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Ehrenmitglieder sind vom Union-Beitrag befreit. Alle anderen aktiven Mitglieder werden im Abrechnungsformular mit diesem Betrag geführt.
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

function Row({ label, sub, children }: { label: string; sub?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {sub && <div className="text-tertiary" style={{ fontSize: 12.5, marginTop: 1 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const strong = scorePassword(pw).score >= 2;
  const match = !!pw && pw === pw2;
  const canSave = strong && match && cur.length > 0;

  const submit = async () => {
    setLoading(true); setError(null);
    try {
      await api.post('/auth/password', { aktuell: cur, neu: pw });
      qc.invalidateQueries({ queryKey: queryKeys.me });
      toast.push('Passwort geändert');
      onClose();
      setCur(''); setPw(''); setPw2('');
    } catch (err) {
      if (err instanceof ApiError && err.payload?.error === 'wrong_current') setError('Das aktuelle Passwort stimmt nicht.');
      else setError('Konnte nicht speichern.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Passwort ändern"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={submit} disabled={!canSave || loading}>Speichern</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Aktuelles Passwort" error={error}>
          <PasswordInput value={cur} onChange={setCur} placeholder="Dein aktuelles Passwort" autoFocus />
        </Field>
        <Field label="Neues Passwort">
          <PasswordInput value={pw} onChange={setPw} placeholder="Mindestens 8 Zeichen" />
        </Field>
        <PasswordStrength pw={pw} />
        <Field label="Passwort wiederholen" error={pw2 && !match ? 'Die Passwörter stimmen nicht überein.' : null}>
          <PasswordInput value={pw2} onChange={setPw2} placeholder="Nochmal eingeben" />
        </Field>
      </div>
    </Modal>
  );
}

function RegenerateCodesButton() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post<{ codes: string[] }>('/auth/recovery-codes/generate');
      setCodes(r.codes);
    } catch {
      toast.push('Konnte Codes nicht generieren', 'warn');
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Button variant="secondary" size="sm" icon={<LifeBuoy size={14} />} onClick={generate} disabled={loading}>
        Neu generieren
      </Button>
      <Modal
        open={!!codes}
        onClose={() => setCodes(null)}
        title="Neue Recovery-Codes"
        footer={<Button variant="primary" onClick={() => setCodes(null)}>Fertig</Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="banner warn" style={{ margin: 0 }}>
            <KeyRound size={18} />
            <div className="banner-text">Die bisherigen Codes sind jetzt ungültig. Speichere die neuen sicher.</div>
          </div>
          <div className="codes-grid">
            {(codes ?? []).map((c, i) => (
              <div className="code-cell" key={i}><span className="idx">{i + 1}</span>{c}</div>
            ))}
          </div>
          <Button variant="secondary" icon={<KeyRound size={14} />} onClick={() => navigator.clipboard?.writeText((codes ?? []).join('\n'))}>
            Alle kopieren
          </Button>
        </div>
      </Modal>
    </>
  );
}

// ----------------------------------------------------------------
// Profile card — Vorname / Nachname / Telefon editierbar
// ----------------------------------------------------------------
function ProfileCard({ user }: { user: CurrentUser }) {
  const update = useUpdateMe();
  const toast = useToast();
  const [vorname, setVorname] = useState(user.vorname);
  const [nachname, setNachname] = useState(user.nachname);
  const [telefon, setTelefon] = useState(user.telefon ?? '');

  useEffect(() => {
    setVorname(user.vorname);
    setNachname(user.nachname);
    setTelefon(user.telefon ?? '');
  }, [user.id, user.vorname, user.nachname, user.telefon]);

  const dirty =
    vorname !== user.vorname || nachname !== user.nachname || (telefon || '') !== (user.telefon ?? '');

  const save = () => {
    if (!dirty) return;
    update.mutate(
      { vorname, nachname, telefon },
      {
        onSuccess: () => toast.push('Profil gespeichert'),
        onError: () => toast.push('Konnte nicht speichern', 'warn'),
      },
    );
  };

  return (
    <Card>
      <CardHeader>Mein Profil</CardHeader>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <Avatar name={initials(vorname, nachname)} size="xl" />
          <div>
            <div className="t-title-3">{vorname} {nachname}</div>
            <div className="text-tertiary" style={{ fontSize: 14 }}>{user.email}</div>
            <div style={{ marginTop: 6 }}><Badge tone="accent" dot>{ROLE_LABEL[user.rolle]}</Badge></div>
          </div>
        </div>
        <div className="grid-2">
          <Field label="Vorname"><Input value={vorname} onChange={(e) => setVorname(e.target.value)} /></Field>
          <Field label="Nachname"><Input value={nachname} onChange={(e) => setNachname(e.target.value)} /></Field>
        </div>
        <Field label="Telefon"><Input className="t-mono" value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="+43 …" /></Field>
        <Field label="E-Mail" hint="E-Mail kann nur ein Admin in der Benutzerverwaltung ändern.">
          <Input value={user.email} className="t-mono" disabled />
        </Field>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={save} disabled={!dirty || update.isPending}>
            Speichern
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ----------------------------------------------------------------
// Sessions list — eigene aktive Sitzungen, einzeln widerrufbar
// ----------------------------------------------------------------
const SESSIONS_PREVIEW = 4;

function SessionsList() {
  const { data, isLoading } = useSessions();
  const revoke = useRevokeSession();
  const toast = useToast();
  const [showAll, setShowAll] = useState(false);

  // Aktuelle Sitzung immer zuerst, damit sie auch eingeklappt sichtbar bleibt.
  const sessions = [...(data?.sessions ?? [])].sort((a, b) => Number(!!b.current) - Number(!!a.current));
  const visible = showAll ? sessions : sessions.slice(0, SESSIONS_PREVIEW);
  const hidden = sessions.length - visible.length;

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Aktive Sitzungen</div>
      {isLoading ? (
        <div className="skeleton" style={{ height: 28 }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visible.map((s) => {
            const ua = s.user_agent ?? '';
            const isMobile = /iPhone|Android|Mobile/i.test(ua);
            const browser =
              /Edg\//.test(ua) ? 'Edge' :
              /Firefox/.test(ua) ? 'Firefox' :
              /Chrome/.test(ua) ? 'Chrome' :
              /Safari/.test(ua) ? 'Safari' :
              'Browser';
            const os =
              /Windows/.test(ua) ? 'Windows' :
              /Mac OS X/.test(ua) ? 'macOS' :
              /Android/.test(ua) ? 'Android' :
              /iPhone|iPad/.test(ua) ? 'iOS' :
              /Linux/.test(ua) ? 'Linux' :
              '';
            return (
              <div
                key={s.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 12.5, color: 'var(--text-tertiary)',
                  padding: '6px 8px', borderRadius: 7,
                  background: s.current ? 'var(--accent-soft)' : 'transparent',
                }}
              >
                {isMobile ? <Smartphone size={13} /> : <Monitor size={13} />}
                <span style={{ flex: 1, color: s.current ? 'var(--accent)' : 'var(--text-secondary)' }}>
                  {browser}{os ? ` · ${os}` : ''}
                  {s.current && <span style={{ marginLeft: 6, fontWeight: 600, fontSize: 11 }}>· diese Sitzung</span>}
                </span>
                <span className="t-mono">{s.ip_address ?? '–'}</span>
                <span className="t-num">{fmtDateTime(s.last_used_at)}</span>
                {!s.current && (
                  <button
                    type="button"
                    title="Sitzung beenden"
                    onClick={() => revoke.mutate(s.id, {
                      onSuccess: () => toast.push('Sitzung beendet'),
                      onError: () => toast.push('Konnte nicht beenden', 'warn'),
                    })}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: 4, borderRadius: 4, color: 'var(--text-tertiary)',
                      display: 'inline-flex',
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
          {sessions.length === 0 && (
            <div className="text-tertiary" style={{ fontSize: 12.5 }}>Keine aktiven Sitzungen.</div>
          )}
          {sessions.length > SESSIONS_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              style={{
                alignSelf: 'flex-start', marginTop: 2,
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '2px 0', fontSize: 12.5, fontWeight: 500, color: 'var(--accent)',
              }}
            >
              {showAll ? 'Weniger anzeigen' : `${hidden} weitere anzeigen`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Master-Token rotieren (Notfall-Schlüssel für Setup-Wizard)
// Wichtig: Klartext wird nur EINMAL angezeigt + muss vom Entwickler
// als Worker-Secret gesetzt werden, sonst funktioniert er noch nicht.
// ----------------------------------------------------------------
function RotateMasterTokenButton() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ plain: string; hash: string; hint: string } | null>(null);

  const reset = () => { setOpen(false); setConfirmed(false); setResult(null); };

  const rotate = async () => {
    setLoading(true);
    try {
      const r = await api.post<{ plain: string; hash: string; hint: string }>('/auth/master-token/rotate');
      setResult(r);
    } catch {
      toast.push('Rotation fehlgeschlagen', 'warn');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="secondary" size="sm" icon={<Siren size={14} />} onClick={() => setOpen(true)}>
        Rotieren
      </Button>
      <Modal
        open={open}
        onClose={reset}
        title={result ? 'Neuer Notfall-Schlüssel' : 'Notfall-Schlüssel rotieren'}
        size="lg"
        footer={
          result ? (
            <Button variant="primary" onClick={reset}>Verstanden, schließen</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={reset}>Abbrechen</Button>
              <Button variant="danger" icon={<Siren size={14} />} onClick={rotate} disabled={!confirmed || loading}>
                Neuen Token erzeugen
              </Button>
            </>
          )
        }
      >
        {!result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="banner warn" style={{ margin: 0 }}>
              <AlertTriangle size={18} />
              <div className="banner-text">
                Der bisherige Notfall-Schlüssel wird sofort ungültig. Stelle vorher sicher, dass:
              </div>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13.5 }}>
              <li>Es existieren <strong>≥ 2 aktive Admins</strong> in der App.</li>
              <li>Der <strong>alte Klartext-Token</strong> aus dem Tresor wird vernichtet, sobald der neue dort liegt.</li>
              <li>Der <strong>neue Hash</strong> muss von einer Person mit Server-Zugriff als Worker-Secret <span className="t-mono">MASTER_RECOVERY_TOKEN_HASH</span> gesetzt werden. Ohne diesen Schritt funktioniert der neue Token <em>nicht</em> für die Ersteinrichtung.</li>
              <li>Diese Aktion wird im Aktivitätsprotokoll als kritisch markiert.</li>
            </ul>
            <label className="row" style={{ gap: 10, cursor: 'pointer', padding: '10px 12px', background: 'var(--bg-muted)', borderRadius: 9 }}>
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>Ich verstehe und übernehme die Verantwortung.</span>
            </label>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="banner warn" style={{ margin: 0 }}>
              <AlertTriangle size={18} />
              <div className="banner-text">
                <strong>Notiere den Klartext jetzt im Vereinstresor</strong> — er wird nur einmal angezeigt.
              </div>
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Klartext (für den Tresor)</div>
              <div className="temp-pw">
                <span className="val">{result.plain}</span>
                <button className="copy" onClick={() => { navigator.clipboard?.writeText(result.plain); toast.push('Kopiert'); }} title="Kopieren">
                  <Copy size={17} />
                </button>
              </div>
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>SHA-256-Hash (für Server-Konfiguration)</div>
              <div className="muted-card" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="t-mono" style={{ fontSize: 12, wordBreak: 'break-all', flex: 1 }}>{result.hash}</span>
                <Button variant="ghost" size="sm" icon={<Copy size={14} />} onClick={() => { navigator.clipboard?.writeText(result.hash); toast.push('Hash kopiert'); }} />
              </div>
              <div className="text-tertiary" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                Der Entwickler/Betreiber muss diesen Hash als Worker-Secret setzen:{' '}
                <span className="t-mono">npx wrangler secret put MASTER_RECOVERY_TOKEN_HASH</span>
              </div>
            </div>
            <div className="muted-card" style={{ fontSize: 12.5 }}>
              {result.hint}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
